<template>
    <div class="glass-container">
        <div class="location-performance-container">
            <div class="modern-card">
                <!-- Header -->
                <div class="header-section">
                    <div class="title-section">
                        <h1 class="page-title">Location Performance Metrics</h1>
                        <p class="page-subtitle">Detailed performance analysis across all locations</p>
                    </div>
                </div>

                <!-- Metrics Cards -->
                <div class="metrics-row">
                    <b-row>
                        <b-col md="3" v-for="metric in metrics" :key="metric.title">
                            <div class="metric-card-glass" :class="metric.variant">
                                <div class="metric-icon">
                                    <b-icon :icon="metric.icon"></b-icon>
                                </div>
                                <div class="metric-content">
                                    <div class="metric-value">{{ metric.value }}</div>
                                    <div class="metric-label">{{ metric.title }}</div>
                                </div>
                            </div>
                        </b-col>
                    </b-row>
                </div>

                <!-- Performance Table -->
                <div class="performance-section">
                    <div class="section-header">
                        <h3 class="section-title">Location Performance Details</h3>
                        <div class="section-actions">
                            <b-button 
                                variant="outline-primary" 
                                size="sm"
                                @click="exportData"
                                class="export-btn"
                            >
                                <b-icon icon="download" class="mr-2"></b-icon>
                                Export Data
                            </b-button>
                        </div>
                    </div>

                    <div class="table-container-glass">
                        <div class="table-responsive">
                            <table class="modern-table">
                                <thead>
                                    <tr>
                                        <th 
                                            v-for="field in locationFields" 
                                            :key="field.key"
                                            class="table-header"
                                            @click="locationSortBy = field.key; locationSortDesc = !locationSortDesc"
                                        >
                                            {{ field.label }}
                                            <span class="sort-indicator" v-if="locationSortBy === field.key">
                                                {{ locationSortDesc ? '↓' : '↑' }}
                                            </span>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr 
                                        v-for="(location, index) in sortedLocationMetrics" 
                                        :key="index"
                                        class="table-row"
                                        :class="getRowClass(location)"
                                    >
                                        <td class="table-cell">
                                            <b-badge variant="" class="state-badge">
                                                    {{ location.location_name }}
                                            </b-badge>
                                        </td>
                                        
                                        <td class="table-cell">
                                            <span class="state-badge">
                                                {{ location.state || 'N/A' }}
                                            </span>
                                        </td>
                                        
                                        <td class="table-cell">
                                            <div class="progress-container">
                                                <div class="progress glass-progress">
                                                    <div 
                                                        class="progress-bar" 
                                                        :style="{ width: getProgressWidth(location.customer_count) + '%' }"
                                                    ></div>
                                                </div>
                                                <span class="progress-value">{{ location.customer_count || 0 }}</span>
                                            </div>
                                        </td>
                                        
                                        <td class="table-cell">
                                            <span class="orders-count">
                                                {{ location.total_orders || 0 }}
                                            </span>
                                        </td>
                                        
                                        <td class="table-cell">
                                            <span class="revenue-value">
                                                ${{ (location.total_revenue || 0).toLocaleString() }}
                                            </span>
                                        </td>
                                        
                                        <td class="table-cell">
                                            <b-badge :variant="getAOVVariant(location.avg_order_value)" class="glass-badge">
                                                ${{ (location.avg_order_value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
                                            </b-badge>
                                        </td>
                                        
                                        <td class="table-cell">
                                            <div class="order-types-breakdown">
                                                <div class="order-type-item pickup">
                                                    <b-icon icon="shop" class="mr-1"></b-icon>
                                                    {{ location.order_type_breakdown.home_delivery || 0 }}
                                                </div>
                                                <div class="order-type-item delivery">
                                                    <b-icon icon="truck" class="mr-1"></b-icon>
                                                    {{ location.order_type_breakdown.store_pickup || 0 }}
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        
                        <!-- Table Footer -->
                        <div class="table-footer" v-if="locationMetrics.length > 0">
                            <div class="pagination-info">
                                Showing {{ locationMetrics.length }} locations sorted by 
                                <strong>{{ getSortFieldLabel() }}</strong>
                                {{ locationSortDesc ? '(Descending)' : '(Ascending)' }}
                            </div>
                        </div>
                        
                        <!-- Empty State -->
                        <div v-else class="empty-state">
                            <div class="empty-state-icon">
                                <b-icon icon="bar-chart" font-scale="4"></b-icon>
                            </div>
                            <h3>No Location Data Available</h3>
                            <p>Location performance data will appear here once available</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'LocationPerformance',
    props: {
        locationData: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            locationSortBy: 'total_revenue',
            locationSortDesc: true,
            locationFields: [
                { key: 'location_id', label: 'Location Name', sortable: true },
                { key: 'state', label: 'State', sortable: true },
                { key: 'customer_count', label: 'Customers', sortable: true },
                { key: 'total_orders', label: 'Total Orders', sortable: true },
                { key: 'total_revenue', label: 'Revenue', sortable: true },
                { key: 'avg_order_value', label: 'Avg Order Value', sortable: true },
                { key: 'order_type_breakdown', label: 'Order Types', sortable: false }
            ]
        };
    },
    computed: {
        locationMetrics() {
            return this.locationData.map(location => ({
                ...location,
                order_type_breakdown: {
                    home_delivery: location.home_delivery_orders || 0,
                    store_pickup: location.store_pickup_orders || 0
                }
            }));
        },
        
        sortedLocationMetrics() {
            const sorted = [...this.locationMetrics];
            
            if (this.locationSortBy === 'customer_count') {
                sorted.sort((a, b) => {
                    const aVal = a.customer_count || 0;
                    const bVal = b.customer_count || 0;
                    return this.locationSortDesc ? bVal - aVal : aVal - bVal;
                });
            } else if (this.locationSortBy === 'total_revenue') {
                sorted.sort((a, b) => {
                    const aVal = a.total_revenue || 0;
                    const bVal = b.total_revenue || 0;
                    return this.locationSortDesc ? bVal - aVal : aVal - bVal;
                });
            } else if (this.locationSortBy === 'avg_order_value') {
                sorted.sort((a, b) => {
                    const aVal = a.avg_order_value || 0;
                    const bVal = b.avg_order_value || 0;
                    return this.locationSortDesc ? bVal - aVal : aVal - bVal;
                });
            } else if (this.locationSortBy === 'total_orders') {
                sorted.sort((a, b) => {
                    const aVal = a.total_orders || 0;
                    const bVal = b.total_orders || 0;
                    return this.locationSortDesc ? bVal - aVal : aVal - bVal;
                });
            } else if (this.locationSortBy === 'state') {
                sorted.sort((a, b) => {
                    const aVal = a.state || '';
                    const bVal = b.state || '';
                    if (this.locationSortDesc) {
                        return bVal.localeCompare(aVal);
                    }
                    return aVal.localeCompare(bVal);
                });
            } else {
                // Default sort by location_id
                sorted.sort((a, b) => {
                    const aVal = a.location_id || 0;
                    const bVal = b.location_id || 0;
                    return this.locationSortDesc ? bVal - aVal : aVal - bVal;
                });
            }
            
            return sorted;
        },
        
        maxCustomers() {
            return Math.max(...this.locationData.map(l => l.customer_count || 0), 1);
        },
        
        maxRevenue() {
            return Math.max(...this.locationData.map(l => l.total_revenue || 0), 1);
        },
        
        metrics() {
            const totalLocations = this.locationData.length;
            const totalRevenue = this.locationData.reduce((sum, l) => sum + (l.total_revenue || 0), 0);
            const avgRevenuePerLocation = totalLocations > 0 ? totalRevenue / totalLocations : 0;
            const topLocation = this.locationData.reduce((max, l) => 
                (l.total_revenue || 0) > (max.total_revenue || 0) ? l : max, { total_revenue: 0 }
            );
            
            const totalCustomers = this.locationData.reduce((sum, l) => sum + (l.customer_count || 0), 0);
            const avgCustomersPerLocation = totalLocations > 0 ? totalCustomers / totalLocations : 0;
            
            return [
                {
                    title: 'Total Locations',
                    value: totalLocations,
                    variant: 'primary',
                    icon: 'shop'
                },
                {
                    title: 'Total Customers',
                    value: totalCustomers.toLocaleString(),
                    variant: 'success',
                    icon: 'people'
                },
                {
                    title: 'Total Revenue',
                    value: `$${totalRevenue.toLocaleString()}`,
                    variant: 'warning',
                    icon: 'cash'
                },
                {
                    title: 'Avg Revenue/Location',
                    value: `$${Math.round(avgRevenuePerLocation).toLocaleString()}`,
                    variant: 'danger',
                    icon: 'graph-up'
                }
            ];
        }
    },
    methods: {
        getAOVVariant(aov) {
            if (aov > 2000) return 'success';
            if (aov > 1000) return 'warning';
            return 'danger';
        },
        
        getProgressWidth(customerCount) {
            if (!customerCount) return 0;
            return Math.min(100, (customerCount / this.maxCustomers) * 100);
        },
        
        getRowClass(location) {
            const revenue = location.total_revenue || 0;
            if (revenue > 1000000) return 'row-high-revenue';
            if (revenue > 500000) return 'row-medium-revenue';
            return 'row-low-revenue';
        },
        
        getSortFieldLabel() {
            const field = this.locationFields.find(f => f.key === this.locationSortBy);
            return field ? field.label : 'Revenue';
        },
        
        exportData() {
            const data = this.sortedLocationMetrics.map(location => ({
                'Location ID': location.location_id,
                'State': location.state || 'N/A',
                'Customers': location.customer_count || 0,
                'Total Orders': location.total_orders || 0,
                'Revenue': location.total_revenue || 0,
                'Average Order Value': location.avg_order_value || 0,
                'Home Delivery Orders': location.order_type_breakdown.home_delivery || 0,
                'Store Pickup Orders': location.order_type_breakdown.store_pickup || 0
            }));
            
            const csv = this.convertToCSV(data);
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            
            link.setAttribute('href', url);
            link.setAttribute('download', `location-performance-${new Date().toISOString().split('T')[0]}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            this.$bvToast.toast('Data exported successfully', {
                title: 'Export Complete',
                variant: 'success',
                solid: true,
                autoHideDelay: 2000
            });
        },
        
        convertToCSV(objArray) {
            const array = typeof objArray !== 'object' ? JSON.parse(objArray) : objArray;
            let str = Object.keys(array[0]).join(',') + '\r\n';
            
            for (let i = 0; i < array.length; i++) {
                let line = '';
                for (let index in array[i]) {
                    if (line !== '') line += ',';
                    line += array[i][index];
                }
                str += line + '\r\n';
            }
            return str;
        }
    }
}
</script>

<style scoped>
.glass-container {
    min-height: 100vh;
    padding: 20px;
    /* background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); */
    background-size: cover;
    background-position: center;
    background-attachment: fixed;
    position: relative;
    overflow: hidden;
}

.glass-container::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
     background: rgba(255, 255, 255, 0.7);
    backdrop-filter: blur(10px);
    z-index: 0;
}

.location-performance-container {
    position: relative;
    z-index: 1;
    min-height: 100vh;
    border-radius: 34px;
}

.modern-card {
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(20px);
    border-radius: 24px;
    box-shadow: 
        0 8px 32px rgba(31, 38, 135, 0.2),
        inset 0 0 0 1px rgba(255, 255, 255, 0.3);
    padding: 30px;
    margin-top: 20px;
    animation: cardAppear 0.6s ease-out;
    position: relative;
    overflow: hidden;
}

.modern-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, 
        transparent 0%, 
        rgba(255, 255, 255, 0.8) 50%, 
        transparent 100%);
}

/* Header Section */
.header-section {
    margin-bottom: 30px;
    padding-bottom: 20px;
    border-bottom: 1px solid rgba(226, 232, 240, 0.5);
    position: relative;
}

.header-section::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 60px;
    height: 3px;
    background: linear-gradient(90deg, #364050,#364050);
    border-radius: 2px;
    animation: slideIn 0.6s ease-out;
}

.title-section {
    animation: slideInUp 0.5s ease-out;
}

.page-title {
    font-size: 2.2rem;
    font-weight: 700;
    background: linear-gradient(135deg, #364050 0%, #364050 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    margin-bottom: 8px;
    text-shadow: 0 2px 10px rgba(102, 126, 234, 0.1);
}

.page-subtitle {
    color: rgba(108, 117, 125, 0.9);
    font-size: 1rem;
    font-weight: 400;
}

/* Metrics Cards Glass Effect */
.metrics-row {
    margin-bottom: 40px;
    animation: fadeIn 0.6s ease-out 0.2s both;
}

.metric-card-glass {
    background: rgba(255, 255, 255, 0.7);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(226, 232, 240, 0.5);
    border-radius: 16px;
    padding: 25px 20px;
    display: flex;
    align-items: center;
    gap: 20px;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;
    box-shadow: 
        0 4px 20px rgba(0, 0, 0, 0.05),
        inset 0 0 0 1px rgba(255, 255, 255, 0.5);
    height: 100%;
    min-height: 120px;
}

.metric-card-glass::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, 
        transparent, 
        rgba(255, 255, 255, 0.3), 
        transparent);
    transition: left 0.6s ease;
}

.metric-card-glass:hover::before {
    left: 100%;
}

.metric-card-glass:hover {
    transform: translateY(-8px);
    box-shadow: 
        0 12px 30px rgba(0, 0, 0, 0.1),
        inset 0 0 0 1px rgba(255, 255, 255, 0.6);
}

.metric-icon {
    width: 60px;
    height: 60px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 1.8rem;
    flex-shrink: 0;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.metric-card-glass.primary .metric-icon {
    background: linear-gradient(135deg, #4299e1 0%, #3182ce 100%);
}

.metric-card-glass.success .metric-icon {
    background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
}

.metric-card-glass.warning .metric-icon {
    background: linear-gradient(135deg, #ed8936 0%, #dd6b20 100%);
}

.metric-card-glass.danger .metric-icon {
    background: linear-gradient(135deg, #f56565 0%, #e53e3e 100%);
}

.metric-content {
    flex: 1;
}

.metric-value {
    font-size: 2rem;
    font-weight: 700;
    color: #2d3748;
    margin-bottom: 5px;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.metric-label {
    font-size: 0.9rem;
    color: rgba(74, 85, 104, 0.8);
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

/* Performance Section */
.performance-section {
    animation: fadeIn 0.6s ease-out 0.4s both;
}

.section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 25px;
    padding-bottom: 15px;
    border-bottom: 1px solid rgba(226, 232, 240, 0.5);
}

.section-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: #2d3748;
    margin: 0;
}

.export-btn {
    background: rgba(255, 255, 255, 0.7);
    backdrop-filter: blur(5px);
    border: 1px solid rgba(102, 126, 234, 0.3);
    color: #667eea;
    border-radius: 10px;
    padding: 8px 16px;
    font-weight: 500;
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
}

.export-btn::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, 
        transparent, 
        rgba(255, 255, 255, 0.3), 
        transparent);
    transition: left 0.6s ease;
}

.export-btn:hover::before {
    left: 100%;
}

.export-btn:hover {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(102, 126, 234, 0.3);
}

/* Table Container Glass Effect */
.table-container-glass {
    background: rgba(255, 255, 255, 0.85);
    backdrop-filter: blur(10px);
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 
        0 4px 20px rgba(0, 0, 0, 0.05),
        inset 0 0 0 1px rgba(255, 255, 255, 0.3);
    margin-bottom: 30px;
}

.modern-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
}

.table-header {
    background: linear-gradient(135deg, 
        rgba(102, 126, 234, 0.1) 0%, 
        rgba(118, 75, 162, 0.1) 100%);
    padding: 18px 20px;
    text-align: left;
    font-weight: 600;
    color: #4a5568;
    font-size: 0.85rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: 2px solid rgba(226, 232, 240, 0.5);
    position: relative;
    cursor: pointer;
    user-select: none;
    transition: all 0.3s ease;
}

.table-header:hover {
    background: linear-gradient(135deg, 
        rgba(102, 126, 234, 0.15) 0%, 
        rgba(118, 75, 162, 0.15) 100%);
}

.table-header::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 0;
    height: 2px;
    background: linear-gradient(90deg, #667eea, #764ba2);
    transition: width 0.3s ease;
}

.table-header:hover::after {
    width: 100%;
}

.sort-indicator {
    margin-left: 8px;
    font-weight: bold;
    color: #667eea;
}

.table-row {
    border-bottom: 1px solid rgba(226, 232, 240, 0.3);
    transition: all 0.3s ease;
    position: relative;
}

.table-row:hover {
    background: rgba(102, 126, 234, 0.05);
    transform: translateX(4px);
}

/* .table-row::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background: linear-gradient(to bottom, transparent, #667eea, transparent);
    opacity: 0;
    transition: opacity 0.3s ease;
} */

.table-row:hover::before {
    opacity: 1;
}

.table-row.row-high-revenue {
    background: linear-gradient(90deg, rgba(56, 161, 105, 0.05), transparent);
}

.table-row.row-medium-revenue {
    background: linear-gradient(90deg, rgba(237, 137, 54, 0.05), transparent);
}

.table-row.row-low-revenue {
    background: linear-gradient(90deg, rgba(245, 101, 101, 0.05), transparent);
}

.table-cell {
    padding: 16px 20px;
    vertical-align: middle;
    color: #4a5568;
    font-size: 0.95rem;
}

/* Badge Glass Effect */
.glass-badge {
    padding: 6px 14px;
    border-radius: 12px;
    font-weight: 600;
    font-size: 0.8rem;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.3);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.glass-badge.badge-secondary {
    background: linear-gradient(135deg, 
        rgba(160, 174, 192, 0.8) 0%, 
        rgba(113, 128, 150, 0.7) 100%);
    color: white;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.glass-badge.badge-success {
    background: linear-gradient(135deg, 
        rgba(56, 161, 105, 0.8) 0%, 
        rgba(56, 161, 105, 0.7) 100%);
    color: white;
    border-color: transparent;
}

.glass-badge.badge-warning {
    background: linear-gradient(135deg, 
        rgba(237, 137, 54, 0.8) 0%, 
        rgba(221, 107, 32, 0.7) 100%);
    color: white;
    border-color: transparent;
}

.glass-badge.badge-danger {
    background: linear-gradient(135deg, 
        rgba(245, 101, 101, 0.8) 0%, 
        rgba(229, 62, 62, 0.7) 100%);
    color: white;
    border-color: transparent;
}

/* State Badge */
.state-badge {
    background: rgba(102, 126, 234, 0.1);
    color: #667eea;
    padding: 4px 12px;
    border-radius: 20px;
    font-weight: 600;
    font-size: 0.85rem;
    border: 1px solid rgba(102, 126, 234, 0.2);
}

/* Progress Bar Glass Effect */
.progress-container {
    display: flex;
    align-items: center;
    gap: 15px;
}

.glass-progress {
    height: 10px;
    border-radius: 5px;
    background: rgba(226, 232, 240, 0.5);
    backdrop-filter: blur(5px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    overflow: hidden;
    flex: 1;
}

.glass-progress .progress-bar {
    background: linear-gradient(90deg, 
        #667eea 0%, 
        #764ba2 100%);
    border-radius: 5px;
    position: relative;
    overflow: hidden;
    transition: width 1s ease;
}

.glass-progress .progress-bar::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(90deg, 
        transparent 0%, 
        rgba(255, 255, 255, 0.3) 50%, 
        transparent 100%);
    animation: shimmer 2s infinite;
}

.progress-value {
    font-weight: 600;
    color: #2d3748;
    min-width: 40px;
    text-align: right;
}

/* Orders Count */
.orders-count {
    font-weight: 700;
    color: #2d3748;
    background: rgba(159, 122, 234, 0.1);
    padding: 6px 12px;
    border-radius: 20px;
    display: inline-block;
}

/* Revenue Value */
.revenue-value {
    font-weight: 700;
    color: #276749;
    font-size: 1.1rem;
}

/* Order Types Breakdown */
.order-types-breakdown {
    display: flex;
    gap: 10px;
}

.order-type-item {
    display: flex;
    align-items: center;
    padding: 6px 12px;
    border-radius: 20px;
    font-size: 0.85rem;
    font-weight: 600;
    border: 1px solid rgba(226, 232, 240, 0.5);
}

.order-type-item.pickup {
    background: rgba(56, 161, 105, 0.1);
    color: #276749;
    border-color: rgba(56, 161, 105, 0.2);
}

.order-type-item.delivery {
    background: rgba(237, 137, 54, 0.1);
    color: #c05621;
    border-color: rgba(237, 137, 54, 0.2);
}

/* Table Footer Glass Effect */
.table-footer {
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(10px);
    padding: 20px;
    border-top: 1px solid rgba(226, 232, 240, 0.5);
    border-radius: 0 0 16px 16px;
}

.pagination-info {
    color: #718096;
    font-size: 0.9rem;
    font-weight: 500;
}

/* Empty State Glass Effect */
.empty-state {
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(10px);
    border-radius: 16px;
    padding: 60px 40px;
    text-align: center;
    box-shadow: 
        0 4px 20px rgba(0, 0, 0, 0.05),
        inset 0 0 0 1px rgba(255, 255, 255, 0.3);
    margin: 20px;
}

.empty-state-icon {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    font-size: 4rem;
    margin-bottom: 20px;
}

.empty-state h3 {
    color: #2d3748;
    margin-bottom: 10px;
    font-weight: 600;
}

.empty-state p {
    color: #718096;
    margin-bottom: 25px;
    font-size: 1rem;
}

/* Animation Keyframes */
@keyframes cardAppear {
    from {
        opacity: 0;
        transform: translateY(20px) scale(0.95);
    }
    to {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
}

@keyframes slideInUp {
    from {
        opacity: 0;
        transform: translateY(30px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes slideInDown {
    from {
        opacity: 0;
        transform: translateY(-30px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes slideIn {
    from {
        width: 0;
    }
    to {
        width: 60px;
    }
}

@keyframes fadeIn {
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
}

@keyframes shimmer {
    0% {
        transform: translateX(-100%);
    }
    100% {
        transform: translateX(100%);
    }
}

/* Responsive Design */
@media (max-width: 768px) {
    .glass-container {
        padding: 10px;
    }
    
    .modern-card {
        padding: 20px;
        border-radius: 20px;
        margin-top: 10px;
    }
    
    .page-title {
        font-size: 1.8rem;
    }
    
    .metric-card-glass {
        margin-bottom: 15px;
        padding: 20px 15px;
        min-height: auto;
    }
    
    .metric-icon {
        width: 50px;
        height: 50px;
        font-size: 1.5rem;
    }
    
    .metric-value {
        font-size: 1.6rem;
    }
    
    .section-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 15px;
    }
    
    .export-btn {
        align-self: flex-start;
    }
    
    .table-container-glass {
        overflow-x: auto;
    }
    
    .modern-table {
        min-width: 800px;
    }
    
    .progress-container {
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
    }
    
    .progress-value {
        text-align: left;
    }
    
    .order-types-breakdown {
        flex-direction: column;
        gap: 5px;
    }
}

/* Scrollbar Styling */
::-webkit-scrollbar {
    width: 10px;
}

::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 5px;
}

::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 5px;
}

::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
}
</style>


// WEBPACK FOOTER //
// src/components/Customers/LocationPerformance.vue